import {
	endOfMonth,
	endOfWeek,
	getLocalTimeZone,
	startOfMonth,
	startOfWeek,
	today,
} from "@internationalized/date";
import { useControlledState } from "@react-stately/utils";
import { Calendar as CalendarIcon } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useDateFormatter } from "react-aria";
import type {
	DateRangePickerProps as AriaDateRangePickerProps,
	DateValue,
} from "react-aria-components";
import {
	DateRangePicker as AriaDateRangePicker,
	Dialog as AriaDialog,
	Group as AriaGroup,
	Popover as AriaPopover,
	useLocale,
} from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { cx } from "@/utils/cx";
import { DateInput } from "./date-input";
import { RangeCalendar } from "./range-calendar";
import { RangePresetButton } from "./range-preset";

const now = today(getLocalTimeZone());

const highlightedDates = [today(getLocalTimeZone())];
type DateRangeValue = { start: DateValue; end: DateValue } | null;

type DateRangePickerProps = AriaDateRangePickerProps<DateValue> & {
	/** The function to call when the apply button is clicked. */
	onApply?: (value: DateRangeValue) => void;
	/** The function to call when the cancel button is clicked. */
	onCancel?: () => void;
	showPresets?: boolean;
	size?: "md" | "sm" | "lg" | "xl";
	buttonClassName?: string;
	/** Shows checkbox to export all data since the first registration date. */
	showPullAllSinceStart?: boolean;
	pullAllSinceStart?: boolean;
	onPullAllSinceStartChange?: (value: boolean) => void;
	pullAllSinceStartLabel?: string;
	pullAllSinceStartHint?: string;
	pullAllSinceStartTriggerLabel?: string;
	selectionError?: string | null;
	validateSelection?: (value: DateRangeValue) => string | null;
};

export const DateRangePicker = ({
	value: valueProp,
	defaultValue,
	onChange,
	onOpenChange,
	onApply,
	onCancel,
	showPresets = false,
	size = "md",
	buttonClassName,
	showPullAllSinceStart = false,
	pullAllSinceStart = false,
	onPullAllSinceStartChange,
	pullAllSinceStartLabel = "Exportar os dados desde o começo",
	pullAllSinceStartHint = "A partir do primeiro dia de cadastro",
	pullAllSinceStartTriggerLabel = "Desde o começo",
	selectionError,
	validateSelection,
	...props
}: DateRangePickerProps) => {
	const { locale } = useLocale();
	const formatter = useDateFormatter({
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
	const [value, setValue] = useControlledState(
		valueProp,
		defaultValue || null,
		onChange
	);
	const [focusedValue, setFocusedValue] = useState<DateValue>(
		() => value?.start ?? now
	);
	const [internalPullAllSinceStart, setInternalPullAllSinceStart] =
		useState(pullAllSinceStart);
	const [isEditing, setIsEditing] = useState(false);
	const [draftValue, setDraftValue] = useState<DateRangeValue>(value);
	const [draftPullAllSinceStart, setDraftPullAllSinceStart] =
		useState(pullAllSinceStart);

	const appliedPullAllSinceStart =
		onPullAllSinceStartChange !== undefined
			? pullAllSinceStart
			: internalPullAllSinceStart;
	const isPullAllSinceStart = isEditing
		? draftPullAllSinceStart
		: appliedPullAllSinceStart;
	const pickerValue = isPullAllSinceStart ? null : isEditing ? draftValue : value;
	const draftError = isPullAllSinceStart
		? null
		: validateSelection
			? validateSelection(draftValue)
			: selectionError;

	const handlePullAllSinceStartChange = (isSelected: boolean) => {
		if (isSelected) {
			setDraftValue(null);
		}

		setDraftPullAllSinceStart(isSelected);
	};

	const formattedStartDate = value?.start
		? formatter.format(value.start.toDate(getLocalTimeZone()))
		: "Selecionar data";
	const formattedEndDate = value?.end
		? formatter.format(value.end.toDate(getLocalTimeZone()))
		: "Selecionar data";

	const triggerLabel = (() => {
		if (appliedPullAllSinceStart) {
			return <span>{pullAllSinceStartTriggerLabel}</span>;
		}
		if (value) {
			return `${formattedStartDate} – ${formattedEndDate}`;
		}
		return <span className="text-placeholder">Selecionar datas</span>;
	})();

	const presets = useMemo(
		() => ({
			today: { label: "Hoje", value: { start: now, end: now } },
			yesterday: {
				label: "Ontem",
				value: {
					start: now.subtract({ days: 1 }),
					end: now.subtract({ days: 1 }),
				},
			},
			thisWeek: {
				label: "Esta semana",
				value: { start: startOfWeek(now, locale), end: endOfWeek(now, locale) },
			},
			lastWeek: {
				label: "Semana passada",
				value: {
					start: startOfWeek(now, locale).subtract({ weeks: 1 }),
					end: endOfWeek(now, locale).subtract({ weeks: 1 }),
				},
			},
			thisMonth: {
				label: "Este mês",
				value: { start: startOfMonth(now), end: endOfMonth(now) },
			},
			lastMonth: {
				label: "Mês passado",
				value: {
					start: startOfMonth(now).subtract({ months: 1 }),
					end: endOfMonth(now).subtract({ months: 1 }),
				},
			},
			thisYear: {
				label: "Este ano",
				value: {
					start: startOfMonth(now.set({ month: 1 })),
					end: endOfMonth(now.set({ month: 12 })),
				},
			},
			lastYear: {
				label: "Ano passado",
				value: {
					start: startOfMonth(now.set({ month: 1 }).subtract({ years: 1 })),
					end: endOfMonth(now.set({ month: 12 }).subtract({ years: 1 })),
				},
			},
			allTime: {
				label: "Todo o período",
				value: {
					start: now.set({ year: 2000, month: 1, day: 1 }),
					end: now,
				},
			},
		}),
		[locale]
	);

	return (
		<AriaDateRangePicker
			aria-label="Date range picker"
			shouldCloseOnSelect={false}
			{...props}
			onChange={(nextValue) => {
				if (isPullAllSinceStart) {
					return;
				}
				setDraftValue(nextValue);
			}}
			onOpenChange={(isOpen) => {
				setIsEditing(isOpen);
				setDraftValue(value);
				setDraftPullAllSinceStart(appliedPullAllSinceStart);
				if (isOpen) {
					setFocusedValue(value?.start ?? now);
				}
				onOpenChange?.(isOpen);
			}}
			value={pickerValue}
		>
			<AriaGroup>
				<Button
					className={cx(buttonClassName, props.isInvalid && 'border-error_subtle ring-error_subtle')}
					color="secondary"
					iconLeading={CalendarIcon}
					size={size}
				>
					{triggerLabel}
				</Button>
			</AriaGroup>
			<AriaPopover
				className={({ isEntering, isExiting }) =>
					cx(
						"origin-(--trigger-anchor-point) will-change-transform",
						!!isEntering &&
							"fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5 animate-in duration-150 ease-out",
						!!isExiting &&
							"fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5 animate-out duration-100 ease-in"
					)
				}
				offset={8}
				placement="bottom right"
			>
				<AriaDialog className="flex rounded-2xl bg-primary shadow-xl ring ring-secondary_alt focus:outline-hidden">
					{({ close }) => (
						<>
							{!!showPresets && (
								<div
									className={cx(
										"hidden w-38 flex-col gap-0.5 border-secondary border-r border-solid p-3 lg:flex",
										!!isPullAllSinceStart && "pointer-events-none opacity-50"
									)}
								>
									{Object.values(presets).map((preset) => (
										<RangePresetButton
											key={preset.label}
											onClick={() => {
												if (isPullAllSinceStart) {
													return;
												}
												setDraftValue(preset.value);
												setFocusedValue(preset.value.start);
											}}
											value={preset.value}
										>
											{preset.label}
										</RangePresetButton>
									))}
								</div>
							)}
							<div className="flex flex-col">
								<div
									className={cx(
										!!isPullAllSinceStart && "pointer-events-none opacity-50"
									)}
								>
									<RangeCalendar
										focusedValue={focusedValue}
										highlightedDates={highlightedDates}
										isReadOnly={isPullAllSinceStart}
										onFocusChange={setFocusedValue}
										presets={{
											lastWeek: presets.lastWeek,
											lastMonth: presets.lastMonth,
											lastYear: presets.lastYear,
										}}
									/>
								</div>
								{showPullAllSinceStart ? (
									<div className="border-secondary px-4 pb-4 md:w-1/2 md:border-r">
										<Checkbox
											hint={pullAllSinceStartHint}
											isSelected={isPullAllSinceStart}
											label={pullAllSinceStartLabel}
											onChange={handlePullAllSinceStartChange}
											size="sm"
										/>
									</div>
								) : null}
								{draftError ? (
									<p
										aria-live="polite"
										className="border-secondary px-4 pb-4 text-error-primary text-sm md:w-1/2 md:border-r"
									>
										{draftError}
									</p>
								) : null}
								<div className="flex justify-between gap-3 border-secondary border-t p-4">
									<div
										className={cx(
											"hidden items-center gap-3 md:flex",
											!!isPullAllSinceStart && "pointer-events-none opacity-50"
										)}
									>
										<DateInput className="w-36" slot="start" />
										<div className="text-md text-quaternary">–</div>
										<DateInput className="w-36" slot="end" />
									</div>
									<div className="grid w-full grid-cols-2 gap-3 md:flex md:w-auto">
										<Button
											color="secondary"
											onClick={() => {
												onCancel?.();
												close();
											}}
											size="md"
										>
											Cancelar
										</Button>
										<Button
											color="primary"
											isDisabled={!!draftError}
											onClick={() => {
												const nextValue = draftPullAllSinceStart ? null : draftValue;
												setValue(nextValue);
												if (draftPullAllSinceStart !== appliedPullAllSinceStart) {
													onPullAllSinceStartChange?.(draftPullAllSinceStart);
													setInternalPullAllSinceStart(draftPullAllSinceStart);
												}
												onApply?.(nextValue);
												close();
											}}
											size="md"
										>
											Aplicar
										</Button>
									</div>
								</div>
							</div>
						</>
					)}
				</AriaDialog>
			</AriaPopover>
		</AriaDateRangePicker>
	);
};
